import React, {
  memo, useEffect, useMemo, useRef, useState,
} from 'react'
import dynamic from 'next/dynamic'
import Image from 'next/image'
import { useRouter } from 'next/router'
import Head from 'next/head'
import Sticky from 'react-sticky-el'
import { FormattedMessage, useIntl } from 'react-intl'
import { formatYears } from 'utils/miscellaneous'
import { useDispatch, useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import pickBy from 'lodash/pickBy'
import Grid from '@mui/material/Grid2'
import Container from '@mui/material/Container'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import { getFilterObject, parseFiltersToUrl } from 'filters'
import {
  setFilters,
  setFilter,
  resetFilters,
  resetFiltersWithOutBody,
  resetVariantFilters,
  setPage,
  // resetYearFilter,
  setFindACarFilters,
  resetFindACarFilters,
  resetAdvanceFilters,
} from 'redux/modules/search'
import EventEmitter from 'services/EventEmitter'
import usePageProps from 'hooks/usePageProps'
import useNonInitialEffect from 'hooks/useNonInitialEffect'
import {
  getAllFiltersSelecteds, getTitleValues, DISCOUNT_SORT,
} from 'utils/filters'
import { getFirst, getSRPSeo } from 'utils/universal/getSRPSeo'
import Text, { TEXT_TYPES } from 'components/designSystem/Text'
import Page from 'components/layout/Page'
import { useModal } from 'components/shared/Modal'
import { useMobile } from 'hooks/useMobile'
import useUtms from 'hooks/useUtms'
import { hasOnlyMakerFilterSelected, hasOnlyMakerModelYearFilterSelected, hasSecondMakerOrModel } from 'utils/vehicles'
import SocialMediaSeoTags from 'components/shared/SocialMediaSeoTags'
import { getSRPath } from 'utils/universal/getVehicles'
import { SRP_TYPE } from 'enums'
import FiltersSelected from 'components/SRP/FiltersSelected/FiltersSelected'
import DiscountConfetti from 'components/SRP/DiscountConfetti'
import { removeValueFromString } from 'utils/universal'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import { applyThousandSeparator } from 'react-number-format/lib/utils'
import { IconButton } from '@mui/material'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import clsx from 'clsx'
import ActionButtons from './Filters/ActionButtons'
import VehicleList from './VehicleList'
import SearchForm from './SearchForm'
import FindACar from './FindACar'
import ButtonsModalsFilters from './FindACar/ButtonsModalsFilters'
import sx from './styles'

const DynamicSortFilters = dynamic(() => import('./Filters/SortFilters'), { ssr: false })

const DynamicFilters = dynamic(() => import('./Filters'), {
  loading: () => (
    <Skeleton
      variant="mobile"
      height="100%"
      animation="wave"
    />
  ),
  ssr: false,
})

const Search = () => {
  const {
    title: titleProp,
    initialFilters: initialFiltersProp,
    titleValues: titleValuesProp,
    page: pageProp,
    srpType = SRP_TYPE.NORMAL,
    isMobile: isMobileServer,
  } = usePageProps()
  const query = useUtms()
  const { formatMessage: f } = useIntl()
  const [titleValues, setTitleValues] = useState(titleValuesProp)
  const [titleFinal, setTitleFinal] = useState(titleProp)
  const [isScrollToTop, setIsScrollToTop] = useState(false)
  const router = useRouter()
  const scrollRef = useRef(null)
  const dispatch = useDispatch()
  const { handleOpenModal, handleCloseModal } = useModal()
  const [isOpenedFilterModal, setIsOpenedFilterModal] = useState(false)
  const { filters, filtersVariant } = useSelector((s) => s.search)
  const basePath = f({ id: 'ROUTE_SEARCH' })
  const srpBasePath = getSRPath(srpType)
  const isMovil = useMobile(isMobileServer)
  const [h1, setH1] = useState(f({ id: titleFinal }, titleValues))
  const filtersSelected = useMemo(() => getAllFiltersSelecteds(filters), [filters])
  const filtersSelectedVariant = useMemo(() => getAllFiltersSelecteds(filtersVariant),
    [filtersVariant])
  const {
    title,
    description,
    ogUrl,
  } = getSRPSeo(
    getFilterObject(router.query?.params, '_'),
  )

  /**
   * ¿El listado viene ordenado por descuento? (`ordenar-discountAmount:desc`)
   *
   * Se lee de la URL y NO de `filters` de redux a proposito: redux se siembra
   * en un useEffect, o sea despues del primer render, asi que el banner
   * aparecería de golpe al hidratar y empujaría el listado hacia abajo. Desde
   * router.query el valor es el mismo en el servidor y en el cliente.
   */
  const isDiscountSort = useMemo(
    () => getFilterObject(router.query?.params, '_')?.sort === DISCOUNT_SORT,
    [router.query?.params],
  )

  const socialMediaTags = {
    ogTitle: title,
    ogDescription: description,
    ogUrl,
    twitterTitle: title,
    twitterDescription: description,
    ogImage: 'https://clikauto-static-files.s3.us-east-2.amazonaws.com/production/website/imagen-srp.webp',
    twitterImage: 'https://clikauto-static-files.s3.us-east-2.amazonaws.com/production/website/imagen-srp.webp',
  }
  const handleEventEmitter = () => {
    setTimeout(() => {
      dispatch(resetFiltersWithOutBody())
    }, 500)
  }

  useEffect(() => {
    EventEmitter.suscribe('logo-click', handleEventEmitter)
    return () => {
      EventEmitter.unsubscribe('logo-click')
    }
  }, [])

  const onCloseFilterModal = () => {
    setIsOpenedFilterModal(false)
    handleCloseModal()
  }

  useEffect(() => {
    router.beforePopState((state) => {
      state.options.scroll = false
      return true
    })
  }, [])

  useEffect(() => {
    if (!router.isReady) return
    const initialFilters = getFilterObject(router.query?.params, '_')
    dispatch(setFilters({ ...initialFilters, ...initialFiltersProp }))
    if (pickBy(initialFilters)) {
      dispatch(setFindACarFilters({ ...initialFilters, ...initialFiltersProp }))
    }
  }, [router.isReady])

  const handleSubmit = (filtersData = {}, closeModal = true, notifyFindACar = false) => {
    const filtersFinal = isEmpty(filtersData) ? filters : filtersData
    if (closeModal) onCloseFilterModal()
    const url = parseFiltersToUrl(filtersFinal)
    router.push(`${srpBasePath}${url}${query}`, null, { shallow: true })
    // setTitleValues(getTitleValues(filtersFinal, true))
    setIsOpenedFilterModal(false)
    dispatch(setFindACarFilters({ ...pickBy(filtersFinal) }))
    if (isMovil && notifyFindACar) {
      EventEmitter.emit('closeFilters')
    }
  }

  const resetModalFilters = () => {
    dispatch(resetFiltersWithOutBody())
    dispatch(resetVariantFilters())
    // setCleanFilterActive(true)
    handleSubmit(filters, false)
  }

  const handleResetFindACarFilters = () => {
    dispatch(resetFilters())
    dispatch(resetVariantFilters())
    dispatch(resetFindACarFilters())
    handleSubmit({ body: '' }, false)
    EventEmitter.emit('resetBodies')
  }

  // const handleRemoveYearFilter = () => {
  //   dispatch(resetYearFilter())
  //   handleSubmit(filters, false)
  // }

  const resetModalMobileFilters = () => {
    dispatch(resetAdvanceFilters())
    dispatch(resetVariantFilters())
    // handleRemoveYearFilter()
    handleSubmit(filters, false)
  }

  useEffect(() => {
    if (!isOpenedFilterModal) {
      setTitleValues(getTitleValues(filters, true))
    }
  }, [filters, isOpenedFilterModal])

  useEffect(() => {
    if (!isOpenedFilterModal) {
      if (hasSecondMakerOrModel(filters)) {
        setH1(f({ id: titleFinal }, { value: '' }))
      } else {
        setH1(f({ id: titleFinal }, titleValues))
      }
    }
  }, [isOpenedFilterModal, titleValues, filters])

  useNonInitialEffect(() => {
    if (!isMovil) {
      handleSubmit(filters, false)
    }
  }, [filters])

  useNonInitialEffect(() => {
    handleSubmit(filters, false)
  }, [filters?.q])

  useNonInitialEffect(() => {
    const isTitleWithYear = hasOnlyMakerModelYearFilterSelected(filters)
    const isOnlyMakerSelected = hasOnlyMakerFilterSelected(filters)
    setTitleFinal(
      isOnlyMakerSelected
        ? 'SEARCH_H1_MAKER'
        : isTitleWithYear ? 'SEARCH_H1_MAKER_MODEL_YEAR' : 'SEARCH_H1',
    )
  }, [filters?.maker, filters?.model, filters?.year])

  useEffect(() => {
    if (router.asPath === basePath && !isEmpty(pickBy(filters))) {
      dispatch(resetFilters())
      dispatch(resetVariantFilters())
    }
  }, [router.asPath])

  useEffect(() => {
    if (pageProp) dispatch(setPage(pageProp))
  }, [])

  const handleClickModal = (filtros) => {
    setIsOpenedFilterModal(true)
    handleOpenModal({
      title: `${f({ id: 'SEARCH_MODAL_FILTERS' }, {
        body: filters?.body ? getFirst(filters?.body) : 'Autos', // TODO: checar si hay algún type de auto seleccionado en el state sino mostrar el 'autos'
      })}`,
      body: (
        <DynamicFilters
          onCloseModal={onCloseFilterModal}
          stickyProps={{
            topOffset: -56,
            boundaryElement: '#search-main-content',
          }}
          testVariant
          filtrosMobileItems={filtros}
        />
      ),
      actionButtons: (
        <ActionButtons
          handleCleanFilters={resetModalMobileFilters}
          onSubmit={handleSubmit}
          filterResetTitle={isMovil ? 'SEARCH_CLEAN_ADVANCE_FILTER' : 'SEARCH_CLEAN_SORT_FILTER'}
          variant="advanced"
        />
      ),
      configProps: {
        isFormattedTitle: false,
        isDividerHeader: true,
        isDividerBottom: true,
        showClose: true,
        maxWidth: 'sm',
        fullScreen: true,
        isMiddleScreen: true,
        variantTitle: 'h2',
      },
      isDismissible: true,
    })
  }

  const handleDeleteAllFilters = () => {
    dispatch(resetFilters())
    dispatch(resetVariantFilters())
    router.push(`${srpBasePath}${query}`, null, { shallow: true })
  }

  // TODO: REFACTOR
  const handleDeleteFilter = (filterItem) => {
    const filtersCopy = { ...filters }
    const filtersVariantCopy = { ...filtersSelectedVariant }

    if (filters.min || filters.max) {
      filtersCopy.priceRange = `precio: $${applyThousandSeparator(filters?.min.toString(), ',')} - $${applyThousandSeparator(filters?.max.toString(), ',')}`
    }
    // delete filtersCopy.min
    // delete filtersCopy.max

    if (filters?.year) {
      filtersCopy.year = `${formatYears(filters?.year)}`
    }
    const cleanFiltersObj = pickBy(filtersCopy) // Remove all keys void
    const filtersVariantCopyVariant = pickBy(filtersVariantCopy)

    // foreach con filtersVariantCopy y hacer un dispatch con
    // removedFilterSelectedVariant para "todos"
    Object.keys(filtersVariantCopyVariant)
      .forEach((filterSelected) => {
        if (`${filtersVariantCopyVariant[filterSelected]}`.includes(`${filterItem}todos`)) {
          const removedFilterSelectedVariant = removeValueFromString(
            filtersVariantCopyVariant[filterSelected],
            filterItem,
          )
          filtersVariantCopyVariant[filterSelected] = removedFilterSelectedVariant
          dispatch(resetVariantFilters({
            key: filterSelected,
            value: removedFilterSelectedVariant,
          }))
        }
      })

    Object.keys(cleanFiltersObj)
      .forEach((filterSelected) => {
        const changeToLowerCaseValue = filterSelected === 'state'
          || filterSelected === 'body'
          || filterSelected === 'sort'
          || filterSelected === 'transmission'
          || filterSelected === 'q'
          || filterSelected === 'instant_delivery'
          || filterSelected === 'warranty_and_satisfaction'
        if (`${cleanFiltersObj[filterSelected]}`.includes(filterItem)) {
          const removedFilterSelected = removeValueFromString(
            cleanFiltersObj[filterSelected],
            filterItem,
            !changeToLowerCaseValue,
          )

          if (filterSelected === 'priceRange') {
            dispatch(setFilter({ key: 'min', value: '' }))
            dispatch(setFilter({ key: 'max', value: '' }))
            dispatch(setFilter({ key: filterSelected, value: removedFilterSelected }))
            dispatch(setPage(1))
          }
          cleanFiltersObj[filterSelected] = removedFilterSelected
          dispatch(setFilter({ key: filterSelected, value: removedFilterSelected }))
          dispatch(setPage(1))
        }
      })
    handleSubmit(cleanFiltersObj, false)
  }

  const handleLoadMore = () => {
    if (scrollRef.current) {
      window.scrollTo({ top: window.scrollY + 10, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    EventEmitter.suscribe('LOAD_MORE', handleLoadMore)
    return () => {
      EventEmitter.unsubscribe('LOAD_MORE')
    }
  }, [])

  const resetModalSortFilters = () => {
    dispatch(setFilters({ sort: '' }))
    handleSubmit(filters, false)
  }

  const handleClickOrderModal = () => {
    setIsOpenedFilterModal(true)
    handleOpenModal({
      title: `${f({ id: 'SEARCH_FILTER_TITLE_SORT_BY_MOBILE' })}`,
      body: (
        <DynamicSortFilters />
      ),
      actionButtons: (
        <ActionButtons
          handleCleanFilters={resetModalSortFilters}
          onSubmit={handleSubmit}
          filterResetTitle="SEARCH_CLEAN_SORT_FILTER"
          variant="sort"
        />
      ),
      configProps: {
        isFormattedTitle: false,
        isDividerHeader: true,
        isDividerBottom: true,
        showClose: true,
        maxWidth: 'sm',
        fullScreen: true,
        isMiddleScreen: true,
        variantTitle: 'h2',
      },
      isDismissible: true,
    })
  }

  const handleScrollTop = () => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    })
  }

  useEffect(() => {
    const handleScroll = () => {
      const shouldBeVisible = window.scrollY > 1200
      if (isScrollToTop !== shouldBeVisible) {
        setIsScrollToTop(shouldBeVisible)
      }
    }

    window.addEventListener('scroll', handleScroll)

    // Limpieza al desmontar el componente
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isScrollToTop])

  return (
    <>
      <Head>
        {title && <title>{title}</title>}
        {description && (
          <meta
            name="description"
            content={description}
          />
        )}
      </Head>
      <Page variant="body-bg-grey">
        <SocialMediaSeoTags {...socialMediaTags} />
        {/* Celebra la llegada a la vista de descuentos. No renderiza marcado:
            administra su propio canvas y se dispara una sola vez por activación. */}
        <DiscountConfetti active={isDiscountSort} />
        <Box
          id="search-root-mobile"
          sx={sx.titleFilter}
          className="variant"
          pt={8}
        >
          {/* <Sticky
                onFixedToggle={toggleInverted}
                topOffset={inverted ? 8 : 0}
                boundaryElement="#search-grid-container"
              > */}
          <Box
            id="search-main-content"
            maxWidth="xl"
            sx={sx.titleFilterContent}
          >
            <Box
              mb={2}
              px={2}
              pt={2}
            >
              <Typography
                sx={sx.searchTitle}
                variant="h2"
                component="h1"
              >
                {h1}
              </Typography>
            </Box>
            <Box sx={sx.stickyContainer}>

              <Grid
                container
                sx={{ px: { xs: isMovil ? 2 : 0 } }}
                maxWidth="xl"
                margin="0 auto"
              >

                <Grid size={12}>
                  <FindACar
                    onSubmit={handleSubmit}
                    onResetFindACarFilters={handleResetFindACarFilters}
                  />
                </Grid>

              </Grid>

            </Box>
          </Box>
          {/* </Sticky> */}

        </Box>

        <Container
          id="search-root"
          maxWidth="xxl"
          sx={sx.root}
          className={isMovil ? 'mobile' : 'variant'}
        >
          <Grid
            id="search-grid-container"
            container
            sx={sx.gridContainer}
            ref={scrollRef}
          >
            <Grid
              size={{
                xs: 12,
                md: 2.9,
              }}
              sx={sx.leftSideContainer}
            >
              <Sticky
                topOffset={-70}
                boundaryElement="#search-root"
              >
                <Grid
                  sx={sx.titleContainer}
                  size={12}
                >
                  <Typography
                    variant="h1"
                    sx={sx.searchTitle}
                  >
                    {h1}
                  </Typography>
                  <Text
                    type={TEXT_TYPES.body.x1}
                    sx={sx.searchTitle}
                  >
                    <FormattedMessage id="SEARCH_FIND_VEHICLE" />
                  </Text>
                  <SearchForm
                    onSubmit={handleSubmit}
                    handleClickModal={handleClickModal}
                    testVariant={!isMovil}
                  />
                </Grid>
                <Divider />
                <Grid
                  size={12}
                  sx={sx.filtersBottomContainer}
                >
                  <DynamicFilters onCloseModal={onCloseFilterModal} />
                </Grid>

                <Grid sx={sx.actionButtons}>
                  <Box mb={2}>
                    <Divider />
                  </Box>
                  <ActionButtons
                    handleCleanFilters={resetModalFilters}
                    onSubmit={handleSubmit}
                  />
                </Grid>

              </Sticky>

            </Grid>

            <Grid
              size={{
                xs: 12,
                md: 9,
              }}
              id="search-main-content"
              pt={isMovil ? 0 : 2}
              sx={sx.listContainer}
            >
              {isMovil && (
                <ButtonsModalsFilters
                  onClickOrderModal={handleClickOrderModal}
                  onClickModal={handleClickModal}
                />
              )}

              {
                (!isMovil) && (
                  <FiltersSelected
                    items={filtersSelected || []}
                    onDeleteItem={handleDeleteFilter}
                    onDeleteAllFilters={handleDeleteAllFilters}
                  />
                )
              }
              {/* Banner promocional. Solo acompaña al ordenamiento por
                  descuento: es la vista donde el usuario ya esta buscando
                  unidades en promocion. */}
              {isDiscountSort && (
                <Box sx={sx.discountBanner}>
                  <Image
                    src="/img/srp/banner_srp.jpg"
                    alt="Últimas horas para estrenar tu seminuevo: bonos de hasta $50 mil y enganche desde 20%"
                    width={1280}
                    height={250}
                    // La columna ocupa todo el ancho en movil y 9/12 en escritorio.
                    sizes="(max-width: 900px) 100vw, 66vw"
                  />
                </Box>
              )}

              <VehicleList
                page={pageProp}
                showAutoslistSchema
              />
            </Grid>
          </Grid>
        </Container>

        <Box
          sx={sx.scrollTopContainer}
          className={clsx({
            visible: isScrollToTop,
          })}
        >
          <IconButton
            aria-label="delete"
            sx={sx.scrollTop}
            onClick={handleScrollTop}
          >
            <ExpandLessIcon />
          </IconButton>
        </Box>

      </Page>
    </>
  )
}

export default memo(Search)
